import React from 'react';
import {
  Alert,
  Button,
  Form,
  ListGroup,
  ProgressBar,
  Spinner,
} from 'react-bootstrap';
import {
  CheckCircle,
  FiletypePdf,
  Upload,
  XCircle,
} from 'react-bootstrap-icons';
import { logInfo } from '../../config_and_helpers/logger';
import './PdfFileUploadPanel.css';

const LOG_CONTEXT = 'PdfFileUploadPanel';
const DEFAULT_MAX_BYTES = 10 * 1024 * 1024;

/**
 * @param {number} bytes
 * @returns {string}
 */
function formatFileSize(bytes) {
  if (bytes === 0) return '0 Bytes';
  const k = 1024;
  const sizes = ['Bytes', 'KB', 'MB', 'GB'];
  const i = Math.floor(Math.log(bytes) / Math.log(k));
  return `${parseFloat((bytes / Math.pow(k, i)).toFixed(2))} ${sizes[i]}`;
}

/**
 * Shared PDF file picker with list UI and red process button.
 * Used by order PDF upload and supplier quote PDF extraction flows.
 *
 * @param {Object} props
 * @param {string} [props.title]
 * @param {boolean} [props.showTitle]
 * @param {string} [props.fileSelectLabel]
 * @param {boolean} [props.multiple]
 * @param {number} [props.maxBytes]
 * @param {string} [props.accept]
 * @param {File[]} props.selectedFiles
 * @param {(files: File[]) => void} props.onSelectedFilesChange
 * @param {(message: string) => void} [props.onValidationError]
 * @param {() => void} props.onProcess
 * @param {boolean} [props.isProcessing]
 * @param {boolean} [props.disabled]
 * @param {string} [props.processButtonLabel]
 * @param {string} [props.processingButtonLabel]
 * @param {string} [props.helperText]
 * @param {string} [props.errorMessage]
 * @param {string} [props.successMessage]
 * @param {number|null} [props.uploadProgress]
 * @param {number|null} [props.currentFileIndex]
 * @param {number|null} [props.totalFileCount]
 * @param {string} [props.className]
 * @param {string} [props.processButtonClassName] - Extra classes on the primary process button
 * @param {React.ReactNode} [props.children] - Optional content below alerts (e.g. review steps)
 */
const PdfFileUploadPanel = ({
  title = 'PDF Upload & Processing',
  showTitle = true,
  fileSelectLabel = 'Select PDF Files',
  multiple = true,
  maxBytes = DEFAULT_MAX_BYTES,
  accept = '.pdf,application/pdf',
  selectedFiles = [],
  onSelectedFilesChange,
  onValidationError,
  onProcess,
  isProcessing = false,
  disabled = false,
  processButtonLabel = 'Upload & Process PDFs',
  processingButtonLabel = 'Processing PDFs...',
  helperText = 'Maximum file size: 10MB per file. Only PDF files are accepted.',
  errorMessage = '',
  successMessage = '',
  uploadProgress = null,
  currentFileIndex = null,
  totalFileCount = null,
  className = '',
  processButtonClassName = '',
  children = null,
}) => {
  const files = Array.isArray(selectedFiles) ? selectedFiles : [];

  const getTotalFileSize = () =>
    files.reduce((total, file) => total + (file?.size || 0), 0);

  const handleFileSelect = event => {
    const picked = Array.from(event.target.files || []);
    event.target.value = '';

    const validFiles = [];
    const errors = [];

    picked.forEach(file => {
      if (file.type !== 'application/pdf') {
        errors.push(`${file.name}: Not a PDF file`);
        return;
      }
      if (file.size > maxBytes) {
        errors.push(`${file.name}: File size must be less than 10MB`);
        return;
      }
      validFiles.push(file);
    });

    if (errors.length > 0) {
      logInfo(LOG_CONTEXT, 'File validation failed', { errors, count: errors.length });
      if (onValidationError) {
        onValidationError(errors.join('\n'));
      }
    } else if (onValidationError) {
      onValidationError('');
    }

    const nextFiles = multiple ? validFiles : validFiles.slice(0, 1);
    onSelectedFilesChange(nextFiles);
  };

  const handleRemoveFile = indexToRemove => {
    onSelectedFilesChange(files.filter((_, index) => index !== indexToRemove));
    if (onValidationError) onValidationError('');
  };

  const handleRemoveAllFiles = () => {
    onSelectedFilesChange([]);
    if (onValidationError) onValidationError('');
  };

  const showProgress =
    isProcessing &&
    uploadProgress != null &&
    currentFileIndex != null &&
    totalFileCount != null &&
    totalFileCount > 0;

  return (
    <div className={`pdf-upload-component ${className}`.trim()}>
      {showTitle && (
        <div className="d-flex align-items-center gap-2 mb-2">
          <FiletypePdf size={20} className="text-danger" />
          <h6 className="mb-0">{title}</h6>
        </div>
      )}

      <div className="border rounded p-3 bg-light">
        <Form.Group className="mb-3">
          <Form.Label className="d-flex align-items-center gap-2">
            <Upload size={16} />
            {fileSelectLabel}
          </Form.Label>
          <Form.Control
            type="file"
            accept={accept}
            multiple={multiple}
            onChange={handleFileSelect}
            disabled={disabled || isProcessing}
            className="form-control-sm"
          />
          {helperText && (
            <Form.Text className="text-muted">{helperText}</Form.Text>
          )}
        </Form.Group>

        {files.length > 0 && (
          <div className="selected-files-container p-2 bg-white rounded border mb-3">
            <div className="d-flex align-items-center justify-content-between mb-2">
              <small className="text-muted">
                {files.length} file(s) selected • Total: {formatFileSize(getTotalFileSize())}
              </small>
              {(multiple || files.length > 1) && (
                <Button
                  variant="outline-danger"
                  size="sm"
                  onClick={handleRemoveAllFiles}
                  disabled={isProcessing}
                  className="btn-sm"
                >
                  Remove All
                </Button>
              )}
            </div>

            {showProgress && (
              <div className="mb-3">
                <div className="d-flex justify-content-between align-items-center mb-1">
                  <small className="text-muted">
                    Processing file {currentFileIndex} of {totalFileCount}
                  </small>
                  <small className="text-muted">
                    {Math.round(uploadProgress)}%
                  </small>
                </div>
                <ProgressBar
                  now={uploadProgress}
                  variant="primary"
                  className="mb-2"
                />
              </div>
            )}

            <ListGroup variant="flush">
              {files.map((file, index) => (
                <ListGroup.Item
                  key={`${file.name}-${file.size}-${index}`}
                  className="d-flex align-items-center justify-content-between py-2"
                >
                  <div className="file-info-container d-flex align-items-center gap-2 flex-grow-1 min-width-0">
                    <FiletypePdf
                      size={16}
                      className="text-danger flex-shrink-0"
                    />
                    <div className="file-details min-width-0 flex-grow-1">
                      <div
                        className="file-name small text-truncate"
                        title={file.name}
                      >
                        {file.name}
                      </div>
                      <div className="file-size text-muted small">
                        ({formatFileSize(file.size)})
                      </div>
                    </div>
                  </div>
                  <Button
                    variant="outline-danger"
                    size="sm"
                    onClick={() => handleRemoveFile(index)}
                    disabled={isProcessing}
                    className="flex-shrink-0 ms-2 d-flex align-items-center justify-content-center"
                    style={{ width: '32px', height: '32px', padding: '0' }}
                  >
                    <XCircle size={14} />
                  </Button>
                </ListGroup.Item>
              ))}
            </ListGroup>
          </div>
        )}

        <Button
          variant={processButtonClassName ? 'secondary' : 'danger'}
          onClick={onProcess}
          disabled={files.length === 0 || disabled || isProcessing}
          className={`w-100 upload-button ${processButtonClassName}`.trim()}
        >
          {isProcessing ? (
            <>
              <Spinner
                as="span"
                animation="border"
                size="sm"
                role="status"
                aria-hidden="true"
                className="me-2"
              />
              {processingButtonLabel}
            </>
          ) : (
            <>
              <Upload size={16} className="me-2" />
              {processButtonLabel}
            </>
          )}
        </Button>

        {successMessage && !errorMessage && (
          <Alert variant="success" className="mt-3 mb-0 py-2">
            <div className="d-flex align-items-center gap-2">
              <CheckCircle size={16} />
              <span className="small">{successMessage}</span>
            </div>
          </Alert>
        )}

        {errorMessage && (
          <Alert variant="danger" className="mt-3 mb-0 py-2">
            <div className="d-flex align-items-start gap-2">
              <XCircle size={16} className="mt-1 flex-shrink-0" />
              <div className="small" style={{ whiteSpace: 'pre-line' }}>
                {errorMessage}
              </div>
            </div>
          </Alert>
        )}

        {children}
      </div>
    </div>
  );
};

export default PdfFileUploadPanel;
